import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from './navbar';
import Footer from './Footer';
import { FiSearch, FiInfo } from 'react-icons/fi';
import { BASE_URL } from '../apiConfig';
import { motion } from 'framer-motion';
import RippleWaveLoader from './ui/RippleWaveLoader';

const ACQUISITION_LOADING_MESSAGES = [
  "Scanning acquisition databases…",
  "Scoring startups by strategic fit…",
  "Analyzing financial health indicators…",
  "Fetching valuation & funding data…",
  "Ranking acquisition targets by score…",
];

// interface ClientProfile {
//   preferred_sectors: string[];
//   max_valuation: string;
//   target_regions: string[];
// }

const StartupAcquisitionPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  // const [clientProfile] = useState<ClientProfile>({
  //   preferred_sectors: ['Manufacturing', 'AI/ML'],
  //   max_valuation: '50M',
  //   target_regions: ['North America', 'Europe']
  // });
  
  const [companiesStatus, setCompaniesStatus] = useState<Record<string, boolean>>({});
  const [results, setResults] = useState<Record<string, any>>({});
  const [end, setEnd] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [prevQuery, setPrevQuery] = useState<string>(''); // Add this state
  const [currentPage, setCurrentPage] = useState<number>(0);
  const ITEMS_PER_PAGE = 6;
  const navigate = useNavigate();
  const restoredRef = useRef(false);

  // Autocomplete state
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeSuggestion, setActiveSuggestion] = useState<number>(-1);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const suggestWrapRef = useRef<HTMLDivElement | null>(null);

  // Restore once on mount (safe parse)
  useEffect(() => {
    if (restoredRef.current) return;
    restoredRef.current = true;

    const safeParse = <T,>(key: string, fallback: T): T => {
      try {
        const v = sessionStorage.getItem(key);
        return v ? JSON.parse(v) as T : fallback;
      } catch {
        return fallback;
      }
    };

    const storedResults = safeParse<Record<string, any>>('acquisitionResults', {});
    const storedCompaniesStatus = safeParse<Record<string, boolean>>('acquisitionCompaniesStatus', {});
    const storedPrevQuery = sessionStorage.getItem('acquisitionPrevQuery') ?? '';
    const storedHasSearched = safeParse<boolean>('acquisitionHasSearched', false);
    const storedEnd = safeParse<boolean>('acquisitionEnd', false);
    const storedCurrentPage = safeParse<number>('acquisitionCurrentPage', 0);

    if (Object.keys(storedResults).length > 0) setResults(storedResults);
    if (Object.keys(storedCompaniesStatus).length > 0) setCompaniesStatus(storedCompaniesStatus);
    setPrevQuery(storedPrevQuery);
    setHasSearched(storedHasSearched);
    setEnd(storedEnd);
    setCurrentPage(storedCurrentPage);
  }, []);

  // Persist on changes
  useEffect(() => {
    sessionStorage.setItem('acquisitionResults', JSON.stringify(results));
    sessionStorage.setItem('acquisitionCompaniesStatus', JSON.stringify(companiesStatus));
    sessionStorage.setItem('acquisitionPrevQuery', prevQuery);
    sessionStorage.setItem('acquisitionHasSearched', JSON.stringify(hasSearched));
    sessionStorage.setItem('acquisitionEnd', JSON.stringify(end));
    sessionStorage.setItem('acquisitionCurrentPage', JSON.stringify(currentPage));
  }, [results, companiesStatus, prevQuery, hasSearched, end, currentPage]);

  // Debounced autocomplete fetch (Brave via backend)
  useEffect(() => {
    const q = searchQuery.trim();

    // Do not show/fetch suggestions while searching
    if (loading || q.length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      setActiveSuggestion(-1);
      return;
    }

    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        setSuggestLoading(true);
        const res = await fetch(`${BASE_URL}/api/autocomplete?q=${encodeURIComponent(q)}`, {
          signal: controller.signal,
        });
        const data = await res.json();
        const list: string[] = Array.isArray(data?.suggestions) ? data.suggestions.slice(0, 8) : [];
        setSuggestions(list);
        setShowSuggestions(list.length > 0);
        setActiveSuggestion(-1);
      } catch (e: any) {
        if (e?.name !== 'AbortError') {
          console.error('[Autocomplete] Error:', e);
        }
      } finally {
        setSuggestLoading(false);
      }
    }, 250);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [searchQuery, loading]); // <- include loading

  const handleSearch = async (inboundQuery?: string) => {
    const effectiveQuery = (inboundQuery ?? searchQuery).trim();
    if (!effectiveQuery) {
      console.log('[Search] Empty query, aborting.');
      return;
    }
    setLoading(true);
    setHasSearched(true);
    setShowSuggestions(false);
    setSuggestions([]);
    setActiveSuggestion(-1);

    const isSameQuery = effectiveQuery === prevQuery.trim();
    // If it's a new query, reset results and page
    if (!isSameQuery) {
      setResults({});
      setCompaniesStatus({});
      setCurrentPage(0);
    }

    const excluded_companies = isSameQuery ? companiesStatus : {};

    console.log('[Search] Current query:', effectiveQuery);
    console.log('[Search] Previous query:', prevQuery);
    console.log('[Search] Is same query?', isSameQuery);
    console.log('[Search] Excluded companies:', excluded_companies);

    try {
      const payload = {
        query: effectiveQuery,
        excluded_companies,
      };
      console.log('[Search] Sending payload:', payload);

      const response = await fetch(`${BASE_URL}/search_acquisition_targets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      console.log('[Search] Response status:', response.status);

      const data = await response.json();
      console.log('[Search] Response data:', data);

      if (isSameQuery) {
        // Append new results to existing ones
        setResults(prevResults => ({
          ...prevResults,
          ...data?.data?.results
        }));
        // Merge new companies status with existing ones
        setCompaniesStatus(prevStatus => ({
          ...prevStatus,
          ...data?.data?.companies_status
        }));
      } else {
        // New query - replace results completely
        setResults(data?.data?.results || {});
        setCompaniesStatus(data?.data?.companies_status || {});
      }

      setEnd(data?.data?.end || false);
      setPrevQuery(effectiveQuery); // Update previous query

      console.log('[Search] Updated state:', {
        query: data?.data?.query || '',
        companiesStatus: data?.data?.companies_status || {},
        results: data?.data?.results || {},
        end: data?.data?.end || false,
        prevQuery: effectiveQuery,
      });
    } catch (error) {
      console.error('[Search] Error:', error);
      setCompaniesStatus({});
      setResults({});
      setEnd(false);
    }
    setLoading(false);
    setSearchQuery('');
  };

  // pageNum is 1-indexed (same convention as StartupSearch)
  const handlePageChange = async (pageNum: number) => {
    if (loading) return;
    const pageIdx = pageNum - 1; // convert to 0-indexed
    const totalLoadedPages = Math.ceil(Object.keys(results).length / ITEMS_PER_PAGE);
    if (pageIdx < totalLoadedPages) {
      // Page already loaded — navigate instantly
      setCurrentPage(pageIdx);
    } else if (!end) {
      // Need to fetch next batch then jump to that page
      setLoading(true);
      try {
        const payload = {
          query: prevQuery,
          excluded_companies: companiesStatus,
        };
        const response = await fetch(`${BASE_URL}/search_acquisition_targets`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        const data = await response.json();
        setResults(prevResults => ({ ...prevResults, ...data?.data?.results }));
        setCompaniesStatus(prevStatus => ({ ...prevStatus, ...data?.data?.companies_status }));
        setEnd(data?.data?.end || false);
        setCurrentPage(pageIdx);
      } catch (error) {
        console.error('[PageChange] Error fetching more results:', error);
      }
      setLoading(false);
    }
  };

  // Helper function at the end of the file
  const getFaviconUrl = (website: string): string | undefined => {
    try {
      const domain = new URL(website).hostname;
      return `https://www.google.com/s2/favicons?domain=${domain}&sz=64`;
    } catch {
      return undefined;
    }
  };

  const handleCardClick = (companyName: string, companyData: any) => {
    // Ensure latest state is stored before leaving
    sessionStorage.setItem('acquisitionResults', JSON.stringify(results));
    sessionStorage.setItem('acquisitionCompaniesStatus', JSON.stringify(companiesStatus));
    sessionStorage.setItem('acquisitionPrevQuery', prevQuery);
    sessionStorage.setItem('acquisitionHasSearched', JSON.stringify(hasSearched));
    sessionStorage.setItem('acquisitionEnd', JSON.stringify(end));
    sessionStorage.setItem('acquisitionCurrentPage', JSON.stringify(currentPage));

    console.log('[Analyze] Navigating to CompanyDetailPage');
    console.log('[Analyze] companyName:', companyName);
    console.log('[Analyze] companyData:', companyData);

    navigate(`/company/${companyName}`, {
      state: { basicInfo: companyData }
    });
  };

  const handleSuggestionSelect = (s: string) => {
    setSearchQuery(s);
    setShowSuggestions(false);
    setSuggestions([]);
    setActiveSuggestion(-1);
    // Ensure we search the selected suggestion
    setTimeout(() => handleSearch(s), 0);
  };

  return (
    <div
      className="min-h-screen relative flex flex-col overflow-x-hidden"
      style={{
        background:
          "radial-gradient(35% 19% at 50% 0%, rgba(255, 84, 31, 0.5) 0%, rgba(0, 0, 0, 0) 100%), black",
        color: "white",
      }}
    >
      <Navbar />

      {/* Mobile-only tweaks for consistent search bar look */}
      <style>{`
        @media (max-width: 640px) {
          .bg-dots { display: none !important; }
          .hero-title { font-size: 32px !important; letter-spacing: -0.5px !important; }
          .hero-subtitle { font-size: 15px !important; padding: 12px 16px !important; }
          .search-bar { height: 44px !important; }
          .search-input { font-size: 16px !important; }
          .search-button { font-size: 16px !important; padding-left: 16px !important; padding-right: 16px !important; }
        }
        input::placeholder { color: rgba(255,255,255,0.28) !important; }
      `}</style>

      {/* Dots Pattern Background (matches Discover page) */}
      <div
        className="absolute bg-dots"
        style={{
          width: "1106px",
          height: "500px",
          top: "-12px",
          left: "194px",
          backgroundImage: "url('/dots.png')",
          backgroundSize: "contain",
          backgroundRepeat: "no-repeat",
          opacity: 1,
          pointerEvents: "none",
          userSelect: "none",
          zIndex: 0,
        }}
      />

      {/* Hero + Search */}
      <div className="w-full flex flex-col items-center justify-center px-4 text-center pt-28 pb-12 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1
            className="mb-3 hero-title"
            style={{
              fontFamily: 'IBM Plex Sans',
              fontWeight: 700,
              fontSize: '48px',
              lineHeight: '110%',
              letterSpacing: '-1px',
              textAlign: 'center'
            }}
          >
            Startup Acquisition Search
          </h1>
          <p
            className="hero-subtitle mx-auto"
            style={{
              fontFamily: 'Lora',
              fontWeight: 400,
              fontSize: '18px',
              lineHeight: '150%',
              textAlign: 'center',
              color: '#9CA3AF',
              borderRadius: '12px',
              maxWidth: '720px',
              margin: '10px auto 0',
              padding: '16px 24px',
              boxShadow: 'none',
              backdropFilter: 'none',
            }}
          >
            Discover potential acquisition targets with our AI-powered search engine. Get detailed insights on startups, their technology, and strategic fit.
          </p>
        </motion.div>

        {/* Search Bar + Autocomplete */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="w-full max-w-2xl mx-auto mt-8"
        >
          <div className="relative w-full" ref={suggestWrapRef}>
            <div className="flex items-center w-full overflow-hidden search-bar" style={{ height: 48 }}>
              <div className="flex-1 flex items-center rounded-l-full border border-[#F9F9F9] bg-black/80 pr-2" style={{ height: '100%' }}>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => !loading && setShowSuggestions(suggestions.length > 0)} // <- gate by loading
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                  onKeyDown={(e) => {
                    if (e.key === 'ArrowDown') {
                      e.preventDefault();
                      setShowSuggestions(true);
                      setActiveSuggestion((idx) => Math.min(idx + 1, suggestions.length - 1));
                      return;
                    }
                    if (e.key === 'ArrowUp') {
                      e.preventDefault();
                      setActiveSuggestion((idx) => Math.max(idx - 1, -1));
                      return;
                    }
                    if (e.key === 'Escape') {
                      setShowSuggestions(false);
                      setActiveSuggestion(-1);
                      return;
                    }
                    if (e.key === 'Enter') {
                      if (activeSuggestion >= 0 && suggestions[activeSuggestion]) {
                        e.preventDefault();
                        const chosen = suggestions[activeSuggestion];
                        handleSuggestionSelect(chosen);
                        return;
                      }
                      handleSearch();
                    }
                  }}
                  placeholder="Search acquisition targets (e.g., AI startups in manufacturing)"
                  className="flex-1 px-6 py-0 outline-none border-none bg-transparent search-input"
                  style={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontWeight: 400,
                    fontSize: 20,
                    lineHeight: '100%',
                    textAlign: 'left',
                    color: '#F9F9F9',
                    height: '100%',
                  }}
                />
                {suggestLoading && (
                  <div className="mr-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white/70" />
                  </div>
                )}
              </div>
              <button
                className="h-full bg-[#FF541F] hover:bg-orange-600 text-white font-semibold px-8 rounded-r-full transition duration-200 search-button flex items-center gap-2"
                style={{ fontSize: 18 }}
                onClick={() => handleSearch()}
                disabled={loading}
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                ) : (
                  <>
                    <FiSearch />
                    Search
                  </>
                )}
              </button>
            </div>

            {/* Autocomplete dropdown */}
            {showSuggestions && !loading && suggestions.length > 0 && ( // <- gate by loading
              <div
                className="absolute left-0 right-0 mt-2 bg-[#0F0F10] border border-white/10 rounded-xl shadow-xl z-20 overflow-hidden"
              >
                {suggestions.map((s, idx) => (
                  <button
                    key={`${s}-${idx}`}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleSuggestionSelect(s)}
                    className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                      idx === activeSuggestion ? 'bg-white/10 text-white' : 'hover:bg-white/5 text-gray-200'
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Quick prompts */}
          <div className="flex flex-wrap gap-2 mt-4 justify-center">
            {[
              "AI startups in healthcare",
              "Manufacturing automation targets",
              "SaaS B2B under $10M valuation",
              "Climate tech startups USA",
              "Profitable fintech acquisition targets",
            ].map((prompt) => (
              <button
                key={prompt}
                onClick={() => { setSearchQuery(prompt); handleSearch(prompt); }}
                className="text-xs px-3 py-1.5 rounded-full border border-white/15 text-gray-400 hover:text-white hover:border-orange-500/50 transition-all"
                style={{ background: "rgba(255,255,255,0.04)" }}
              >
                {prompt}
              </button>
            ))}
          </div>

          {/* Show search query used */}
          {prevQuery && (
            <div className="mt-4 text-left">
              <div className="inline-flex items-start gap-2 text-xs text-gray-300 bg-white/5 border border-white/10 rounded-md px-3 py-2">
                <FiInfo className="mt-0.5 text-blue-300 shrink-0" />
                <div>
                  <span className="text-gray-400">Search query used:</span>{' '}
                  <span className="text-gray-200 break-all">{prevQuery}</span>
                </div>
              </div>
            </div>
          )}

          {/* Show empty message just below search bar */}
          {hasSearched && Object.keys(results).length === 0 && !loading && (
            <div className="flex flex-col items-center justify-center w-full mt-6">
              <div className="bg-zinc-900/80 border border-white/10 rounded-xl px-6 py-8 text-center shadow-lg w-full">
                <h2 className="text-lg font-semibold text-white mb-2">No acquisition targets found</h2>
                <p className="text-gray-400 text-sm">
                  We couldn't find any startups matching your search. <br />
                  Try adjusting your keywords or sector preferences.
                </p>
              </div>
            </div>
          )}

          {/* Show loading state */}
          {loading && (
            <div className="w-full mt-12">
              <RippleWaveLoader messages={ACQUISITION_LOADING_MESSAGES} />
            </div>
          )}
        </motion.div>
        {/* ^ Close the max-w-2xl wrapper so results are not constrained */}

        {/* Results (full-width container) */}
        {results && Object.keys(results).length > 0 && (
          <div className="w-full px-4 md:px-8 mt-12 max-w-7xl mx-auto">
            <motion.div
              className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              {(() => {
                // Deduplicate website URLs across all loaded results
                const seenWebsites = new Set<string>();
                const deduped = Object.entries(results).map(([name, d]: [string, any]) => {
                  const w = (d?.website || '').trim();
                  if (w && seenWebsites.has(w)) return [name, { ...d, website: '' }];
                  if (w) seenWebsites.add(w);
                  return [name, d];
                });
                // Slice to current page
                const paged = deduped.slice(currentPage * ITEMS_PER_PAGE, (currentPage + 1) * ITEMS_PER_PAGE);
                return (paged as [string, any][]).map(([companyName, data]) => (
                <motion.div
                  key={companyName}
                  className="relative rounded-2xl p-6 backdrop-blur-sm border border-white/10 hover:border-white/20 transition-all duration-300 hover:shadow-2xl hover:shadow-orange-500/10 hover:-translate-y-1 cursor-pointer flex flex-col"
                  whileHover={{ scale: 1.02 }}
                  style={{
                    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.03) 0%, rgba(255, 255, 255, 0.01) 100%)',
                    minHeight: 320,
                  }}
                  onClick={() => handleCardClick(companyName, data)}
                >
                  {/* Logo and Name */}
                  <div className="flex items-start gap-4 mb-4">
                    {/* Logo */}
                    {data.website ? (
                      <div className="w-16 h-16 rounded-xl overflow-hidden bg-gradient-to-br from-white/10 to-white/5 border border-white/10 flex items-center justify-center flex-shrink-0">
                        <img
                          src={getFaviconUrl(data.website)}
                          alt={`${companyName} logo`}
                          className="w-12 h-12 object-contain"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            target.parentElement!.innerHTML = `
                              <div class="w-full h-full flex items-center justify-center text-2xl font-bold text-white">
                                ${companyName.charAt(0)}
                              </div>
                            `;
                          }}
                        />
                      </div>
                    ) : (
                      <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-orange-500/20 to-orange-600/10 border border-orange-500/20 flex items-center justify-center flex-shrink-0">
                        <span className="text-2xl font-bold text-white">
                          {companyName.charAt(0)}
                        </span>
                      </div>
                    )}

                    {/* Company Name */}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-bold text-white text-left group-hover:text-orange-400 transition-colors duration-200 line-clamp-2" style={{ fontFamily: 'Sora' }}>
                        {companyName}
                      </h3>
                    </div>
                  </div>

                  {/* Description — fallback chain */}
                  {(() => {
                    const desc =
                      data.strategy?.market_focus ||
                      data.strategy?.competitive_advantage ||
                      data.strategy?.differentiators ||
                      data.basic_info?.sector ||
                      (data.technology_stack?.core_technologies?.length > 0
                        ? `Core tech: ${data.technology_stack.core_technologies.slice(0, 3).join(', ')}`
                        : '') ||
                      '';
                    return desc ? (
                      <div className="mb-3">
                        <p className="text-sm text-gray-400 text-left line-clamp-3" style={{ fontFamily: 'Lora' }}>
                          {desc}
                        </p>
                      </div>
                    ) : null;
                  })()}

                  {/* Info tags */}
                  <div className="flex flex-wrap gap-2 mb-3">
                    {data.basic_info?.sector && (
                      <span className="px-2 py-0.5 text-xs bg-[#FF541F]/20 text-[#FF541F] rounded">{data.basic_info.sector}</span>
                    )}
                    {data.basic_info?.headquarters && (
                      <span className="px-2 py-0.5 text-xs bg-white/10 text-gray-300 rounded">{data.basic_info.headquarters}</span>
                    )}
                    {data.basic_info?.founded_year && (
                      <span className="px-2 py-0.5 text-xs bg-white/10 text-gray-300 rounded">Est. {data.basic_info.founded_year}</span>
                    )}
                    {!data.basic_info?.sector && data.technology_stack?.languages?.slice(0, 2).map((lang: string) => (
                      <span key={lang} className="px-2 py-0.5 text-xs bg-blue-500/20 text-blue-300 rounded">{lang}</span>
                    ))}
                  </div>

                  {/* Website Link */}
                  {data.website && (
                    <div className="mb-4">
                      <a
                        href={data.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-sm text-blue-400 hover:text-blue-300 transition-colors"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                        {new URL(data.website).hostname.replace('www.', '')}
                      </a>
                    </div>
                  )}

                  {/* Spacer to push button to bottom */}
                  <div className="flex-grow"></div>

                  {/* Analyze Button */}
                  <button
                    className="mt-4 w-full px-4 py-3 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold rounded-xl transition-all duration-200 shadow-lg shadow-orange-500/20 hover:shadow-orange-500/30"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCardClick(companyName, data);
                    }}
                    style={{ fontFamily: 'Sora' }}
                  >
                    Analyze Company
                  </button>
                </motion.div>
              ))
              })()}
            </motion.div>
          </div>
        )}

        {/* Show empty state (when not loading and no results) */}
        {!loading && results && Object.keys(results).length === 0 && (
          <div className="w-full max-w-2xl mx-auto mt-12 px-4 text-center">
            <p className="text-gray-400">No companies found matching your search criteria.</p>
          </div>
        )}

        {/* Pagination — matches Startup Directory style */}
        {(() => {
          const totalItems = Object.keys(results).length;
          const totalLoadedPages = Math.ceil(totalItems / ITEMS_PER_PAGE);
          const displayPage = currentPage + 1; // 1-indexed for display
          // Show loaded page numbers + one extra slot if more results exist
          const totalPages = !end ? totalLoadedPages + 1 : totalLoadedPages;
          return totalItems > 0 ? (
            <>
              <div className="flex justify-center items-center gap-2 mt-8 mb-4 flex-wrap">
                {/* Previous */}
                <button
                  onClick={() => handlePageChange(displayPage - 1)}
                  disabled={displayPage === 1}
                  className={`px-4 py-2 rounded-lg font-medium border ${
                    displayPage === 1
                      ? 'bg-black/40 text-gray-500 border-white/10 cursor-not-allowed'
                      : 'bg-[#181818] text-white border-white/10 hover:bg-[#222]'
                  }`}
                >
                  Previous
                </button>

                {/* Numbered page buttons */}
                {[...Array(totalPages)].map((_, idx) => {
                  const pageNum = idx + 1;
                  const isLoaded = pageNum <= totalLoadedPages;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      disabled={loading || (!isLoaded && end)}
                      className={`w-10 h-10 rounded-lg font-medium border ${
                        displayPage === pageNum
                          ? 'bg-[#FF541F] text-white border-transparent'
                          : 'bg-[#181818] text-white border-white/10 hover:bg-[#222]'
                      }`}
                    >
                      {loading && pageNum === totalLoadedPages + 1 ? '...' : pageNum}
                    </button>
                  );
                })}

                {/* Next */}
                <button
                  onClick={() => handlePageChange(displayPage + 1)}
                  disabled={loading || (end && displayPage >= totalLoadedPages)}
                  className={`px-4 py-2 rounded-lg font-medium border ${
                    loading || (end && displayPage >= totalLoadedPages)
                      ? 'bg-black/40 text-gray-500 border-white/10 cursor-not-allowed'
                      : 'bg-[#181818] text-white border-white/10 hover:bg-[#222]'
                  }`}
                >
                  {loading ? 'Loading...' : 'Next'}
                </button>
              </div>

              {/* Item range counter */}
              <div className="text-center text-gray-400 text-sm mt-2 mb-24 pb-8">
                Showing {(currentPage) * ITEMS_PER_PAGE + 1} to{' '}
                {Math.min((currentPage + 1) * ITEMS_PER_PAGE, totalItems)} of{' '}
                {totalItems}{!end ? '+' : ''} results
              </div>
            </>
          ) : null;
        })()}

      </div>

      {/* Add this empty div when there are no results to push footer down */}
      {Object.keys(results).length === 0 && (
        <div className="flex-grow min-h-[50vh]"></div>
      )}

      <Footer />
    </div>
  );
};

export default StartupAcquisitionPage;